import { Injectable } from '@angular/core';
import { ShipmentDocument, ShipmentDocumentCategory } from '../models/documents';
import { environment } from '../../../../environments/environment';
import { Observable, shareReplay, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';

interface UploadQueryParams {
  category: string;
  file: File;
  shipmentNumber: string;
  overwrite?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class DocumentsService {
  private documentCategories$: Observable<ShipmentDocumentCategory[]> | null = null;
  private refreshDocumentList$ = new Subject<void>();

  constructor(private http: HttpClient) {}

  getDocumentCategories() {
    if (!this.documentCategories$) {
      this.documentCategories$ = this.http
        .get<ShipmentDocumentCategory[]>(`${environment.apiUrl}/shipmentDocumentCategories`)
        .pipe(shareReplay());
    }
    return this.documentCategories$;
  }

  getAllDocuments(shipmentNumber: string) {
    return this.http.get<ShipmentDocument[]>(`${environment.apiUrl}/shipments/${shipmentNumber}/documents`);
  }

  getAllArchivedDocuments(shipmentNumber: string) {
    return this.http.get<ShipmentDocument[]>(`${environment.apiUrl}/archive/shipments/${shipmentNumber}/documents`);
  }

  getDocument(shipmentNumber: string, id: string): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/shipments/${shipmentNumber}/documents/${id}`, {
      responseType: 'blob'
    });
  }

  getManyDocuments(shipmentNumber: string, documentIds: string[]) {
    return this.http.post(
      `${environment.apiUrl}/shipments/${shipmentNumber}/documents/download`,
      { documentIds },
      { responseType: 'blob' }
    );
  }

  getArchivedDocument(shipmentNumber: string, id: string): Observable<Blob> {
    return this.http.get(`${environment.apiUrl}/archive/shipments/${shipmentNumber}/documents/${id}`, {
      responseType: 'blob'
    });
  }

  getManyArchivedDocuments(shipmentNumber: string, documentIds: string[]): Observable<Blob> {
    return this.http.post(
      `${environment.apiUrl}/archive/shipments/${shipmentNumber}/documents/download`,
      { documentIds },
      { responseType: 'blob' }
    );
  }

  deleteDocument(shipmentNumber: string, id: string) {
    return this.http.delete(`${environment.apiUrl}/shipments/${shipmentNumber}/documents/${id}`);
  }

  refreshDocuments() {
    this.refreshDocumentList$.next();
  }

  onRefreshDocumentList() {
    return this.refreshDocumentList$.asObservable();
  }

  upload({ file, shipmentNumber, category, overwrite }: UploadQueryParams) {
    const formData = new FormData();
    formData.append('formFile', file);
    formData.append('category', category);
    if (overwrite) {
      formData.append('overwrite', 'true');
    }

    return this.http.post<ShipmentDocument>(`${environment.apiUrl}/shipments/${shipmentNumber}/documents`, formData);
  }
}
